import { useState } from "react";
import PublicHeader from "@/components/layout/PublicHeader";
import { usePageTitle } from "@/lib/seo";
import { Link } from "react-router-dom";
import { TrendingUp, Mail, FileText, ArrowRight, Calendar, ShieldCheck, X, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function RequestModal({ report, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [org, setOrg] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/investors/request", {
        name, email, phone, organization: org, report, notes,
      });
      setDone(true);
      toast.success("Request sent — we'll reply within 2 business days.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send your request. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not send your request.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/60 backdrop-blur-sm p-0 sm:p-4" data-testid="request-modal" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-sky-700">Request</div>
            <div className="font-display text-lg font-extrabold text-slate-900 mt-1 truncate">{report}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="flex-none p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" data-testid="request-close">
            <X className="w-5 h-5"/>
          </button>
        </div>
        {done ? (
          <div className="p-8 text-center" data-testid="request-done">
            <div className="w-14 h-14 rounded-full bg-sky-50 text-sky-700 flex items-center justify-center mx-auto">
              <Check className="w-7 h-7"/>
            </div>
            <h3 className="font-display text-xl font-extrabold text-slate-900 mt-4">Request sent!</h3>
            <p className="text-sm text-slate-600 mt-2">A confirmation is in your inbox. We&apos;ll reply within 2 business days.</p>
            <button type="button" onClick={onClose} className="mt-6 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-all">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div>
              <label className="text-xs font-bold text-slate-700">Full name</label>
              <input data-testid="request-name" required value={name} onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 outline-none text-sm"/>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5"><span>Email</span><span className="text-rose-600 font-bold ml-0.5">*</span></label>
                <input data-testid="request-email" type="email" required aria-required="true" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 outline-none text-sm"/>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700">Phone <span className="text-slate-400 font-normal">(optional)</span></label>
                <input data-testid="request-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 outline-none text-sm"/>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700">Organization <span className="text-slate-400 font-normal">(optional)</span></label>
              <input data-testid="request-org" value={org} onChange={(e) => setOrg(e.target.value)}
                className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 outline-none text-sm"/>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700">Notes <span className="text-slate-400 font-normal">(optional)</span></label>
              <textarea data-testid="request-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 outline-none text-sm resize-y"
                placeholder="Anything we should know about your interest."/>
            </div>
            <button data-testid="request-submit" type="submit" disabled={busy}
              className="w-full px-6 py-3 rounded-full bg-sky-600 text-white font-bold text-sm shadow hover:bg-sky-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all">
              {busy ? "Sending…" : "Send request"}
            </button>
            <p className="text-[11px] text-slate-500 text-center">
              Routed to contact@centralrentals.ca. We reply within 2 business days.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

const REPORTS = [
  { label: "Latest quarterly briefing", date: "Available on request", type: "Quarterly update" },
  { label: "Annual letter to investors", date: "Available on request", type: "Annual letter" },
  { label: "Capital partnership memo", date: "Available on request", type: "Capital update" },
];

const FAQS = [
  {
    q: "Is Central Rentals publicly traded?",
    a: "No. We're an independent Canadian corporation. We accept new investors selectively under SAFE notes for accredited investors.",
  },
  {
    q: "Who has invested in Central Rentals so far?",
    a: "We're backed by a syndicate of Canadian PropTech operators and angels across Canada. We do not publish the investor list — please request an introduction below.",
  },
  {
    q: "How does Central Rentals make money?",
    a: "Monthly subscription revenue (DIY $5, Starter $35, Growth $50, Enterprise $80 CAD). We do not take payment processing fees on rent collected via our Stripe Connect integration — that goes straight to the landlord.",
  },
  {
    q: "What are your KPIs?",
    a: "Net Revenue Retention, gross margin, and CAC payback are our north-star metrics. Detailed figures are shared with prospective investors under NDA.",
  },
];

export default function Investors() {
  usePageTitle("Investor Relations — Central Rentals Canada");
  const [requestFor, setRequestFor] = useState(null);
  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />
      {requestFor && <RequestModal report={requestFor} onClose={() => setRequestFor(null)}/>}

      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-br from-sky-50 via-white to-indigo-50">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-24" data-testid="investors-hero">
          <div className="text-xs tracking-[0.22em] uppercase font-bold text-sky-700">Investor Relations</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 mt-3">
            The operating system for Canadian rentals.
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-3xl leading-relaxed">
            An independent Canadian software company serving small landlords across all 13
            provinces and territories. We share updates with our investors every
            quarter — and openly with the public when we can.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <button type="button" onClick={() => setRequestFor("Investor introduction")} data-testid="investors-contact-cta"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-sky-600 text-white font-bold text-sm shadow hover:bg-sky-700 transition-all">
              <Mail className="w-4 h-4"/> Request an introduction
            </button>
            <a href="#reports" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-slate-300 text-slate-900 font-bold text-sm hover:bg-slate-50 transition-all">
              <FileText className="w-4 h-4"/> View reports
            </a>
          </div>
        </div>
      </section>

      {/* Metrics */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">What we focus on</div>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mt-2">
            The numbers that matter.
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10 text-center">
            {[
              { n: "13", l: "Provinces & territories supported", note: "Canada-wide coverage" },
              { n: "100%", l: "Recurring revenue", note: "Subscription model" },
              { n: "85%", l: "Target gross margin", note: "Software economics" },
              { n: "<5 mo", l: "CAC payback target", note: "Efficient growth" },
            ].map(({ n, l, note }) => (
              <div key={l} className="rounded-2xl border border-slate-200 p-5">
                <div className="font-display text-2xl md:text-3xl font-extrabold text-sky-700">{n}</div>
                <div className="text-sm text-slate-700 font-semibold mt-1">{l}</div>
                <div className="text-xs text-slate-500 mt-0.5">{note}</div>
              </div>
            ))}
          </div>
          <div className="text-xs text-slate-500 mt-6">
            Current revenue, growth rate, retention, and unit-economics figures are shared with prospective investors under NDA.
            <button type="button" onClick={() => setRequestFor("Investor introduction")} className="text-emerald-700 underline ml-1" data-testid="investors-metrics-cta">Request an introduction</button>.
          </div>
        </div>
      </section>

      {/* Reports */}
      <section id="reports" className="bg-slate-50 border-y border-slate-200 py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Investor reports &amp; updates</div>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mt-2">
            Latest reports.
          </h2>
          <div className="mt-8 space-y-3" data-testid="investors-reports-list">
            {REPORTS.map(r => (
              <div key={r.label} className="flex items-center justify-between gap-4 rounded-2xl bg-white border border-slate-200 p-5 hover:border-sky-300 transition-all">
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-sky-700">{r.type}</div>
                  <div className="font-display text-base md:text-lg font-bold text-slate-900 mt-1 truncate">{r.label}</div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1">
                    <Calendar className="w-3.5 h-3.5"/>{r.date}
                  </div>
                </div>
                <button type="button" onClick={() => setRequestFor(r.label)} data-testid={`report-request-${r.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="flex-none inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-all">
                  Request <ArrowRight className="w-3.5 h-3.5"/>
                </button>
              </div>
            ))}
          </div>
          <div className="text-xs text-slate-500 mt-5">All reports shared upon request to verified investors and partners.</div>
        </div>
      </section>

      {/* Why Central Rentals */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Why this matters</div>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mt-2">
            A Canadian software market that&apos;s still mostly spreadsheets.
          </h2>
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {[
              { icon: TrendingUp, t: "Underserved market", d: "Millions of rental units owned by independent Canadian landlords — most still on paper, spreadsheets, or US-built tools." },
              { icon: ShieldCheck, t: "Regulatory moat", d: "Each province has different RTA, tax forms, and notice periods. We've built every one of them — a multi-year head start on US entrants." },
              { icon: TrendingUp, t: "Efficient growth", d: "Pure subscription economics: recurring revenue, high gross margin, low support cost per door. We grow with discipline." },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className="rounded-2xl border border-slate-200 p-6">
                <div className="w-11 h-11 rounded-xl bg-sky-50 text-sky-700 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5"/>
                </div>
                <div className="font-display text-base font-bold text-slate-900">{t}</div>
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 border-y border-slate-200 py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 text-center">FAQ</div>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 text-center mt-2">Common investor questions.</h2>
          <div className="mt-8 space-y-3">
            {FAQS.map(({ q, a }) => (
              <details key={q} className="group rounded-2xl bg-white border border-slate-200 p-5">
                <summary className="cursor-pointer flex items-center justify-between gap-3 font-semibold text-slate-900">
                  {q}
                  <span className="text-slate-400 group-open:rotate-180 transition-transform">▾</span>
                </summary>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-12 md:py-16 border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900">Get in touch.</h2>
          <p className="mt-3 text-slate-600">For investor introductions, capital partnerships, or strategic discussions.</p>
          <button type="button" onClick={() => setRequestFor("Investor introduction")}
            data-testid="investors-bottom-contact"
            className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-sky-600 text-white font-bold text-base hover:bg-sky-700 transition-all">
            <Mail className="w-4 h-4"/> Request an introduction
          </button>
          <div className="mt-3 text-xs text-slate-500">
            We reply within 2 business days. <Link to="/about" className="text-emerald-700 underline">Learn more about who we are</Link>.
          </div>
        </div>
      </section>
    </div>
  );
}
